// Contract between the SPA and the BFF (/api/*). Pony-agnostic.

export type VehicleType = "ebike" | "bike" | "scooter" | "unknown";

export interface Vehicle {
  /** Map key. Equals vehicleId when known; otherwise derived from region + position (display only). */
  id: string;
  /** Stable Pony id (GBFS `_vehicle_id`), the only id that can be booked or put in a URL. */
  vehicleId?: string;
  type: VehicleType;
  lat: number;
  lng: number;
  battery: number; // 0..1
  rangeMeters: number;
  stationId?: string;
  /** GBFS pricing_plan_id, key of GET /api/pricing. */
  pricingPlanId?: string;
}

/** GET /api/pricing: plans by id, with the label to display. */
export type PricingPlans = Record<string, import("./pricing").PricingPlan & { label: string }>;

export interface ParkingZone {
  id: string;
  name: string;
  lat: number;
  lng: number;
  polygon?: [number, number][]; // [lng, lat]
}

export interface ForbiddenZone {
  id: string;
  name: string;
  polygon: [number, number][];
}

export interface ParkingResponse {
  parking: ParkingZone[];
  forbidden: ForbiddenZone[];
  /** Every geofencing zone (operating, slow, no parking, no ride), see shared/zones.ts. */
  zones?: import("./zones").Zone[];
}

export interface Me {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  activated: boolean; // accepted the bike service terms (Figma 04)
  hasPaymentMethod: boolean;
  ponyStatus: "CREATED" | "OTP_REQUIRED" | "ACTIVE" | "BLOCKED" | "UNKNOWN";
  /** Masked phone of the Pony account, once one is registered ("06 •• •• •• 78"). */
  phone?: string;
}

/** French numbers as typed ("06 12 34 56 78", "+33 6…") → E.164; other "+" numbers kept. */
export function toE164(raw: string): string | undefined {
  const s = raw.replace(/[\s.\-()]/g, "");
  const e164 = /^0[1-9]\d{8}$/.test(s)
    ? `+33${s.slice(1)}`
    : s.startsWith("00")
      ? `+${s.slice(2)}`
      : s;
  return /^\+[1-9]\d{6,14}$/.test(e164) ? e164 : undefined;
}

/** Pony sends SMS codes to French mobiles only (pony-core tomp_sms.allowed_numbers: +336, +337). */
export function toFrenchMobile(raw: string): string | undefined {
  const e164 = toE164(raw);
  return e164 && /^\+33[67]\d{8}$/.test(e164) ? e164 : undefined;
}

export function maskPhone(e164: string): string {
  if (/^\+33\d{9}$/.test(e164)) return `0${e164[3]} •• •• •• ${e164.slice(-2)}`;
  return `${e164.slice(0, 3)} •••• ${e164.slice(-2)}`;
}

/** Stable error codes shown to the client. Never Pony's raw text. */
export type ApiErrorCode =
  | "UNAUTHENTICATED"
  | "FORBIDDEN"
  | "VALIDATION"
  | "RATE_LIMITED"
  | "NOT_FOUND"
  | "PONY_UNAVAILABLE"
  | "ACCOUNT_LINK_FAILED"
  | "VEHICLE_NOT_AVAILABLE"
  | "ONGOING_BOOKING"
  | "UNKNOWN_VEHICLE"
  | "CUSTOMER_BLOCKED"
  | "BOOKING_EXPIRED"
  | "VEHICLE_NO_LONGER_AVAILABLE"
  | "VEHICLE_NOT_RESPONDING"
  | "FORBIDDEN_PARKING"
  | "STRICT_PARKING"
  | "JAUGE_FAILED_STRICT"
  | "ILLEGAL_STATE"
  | "PAYMENT_METHOD_REQUIRED"
  | "PAYMENT_FAILED"
  | "NO_ONGOING_TRIP"
  | "INVALID_CODE"
  | "CODE_EXPIRED"
  | "MAIL_FAILED"
  | "PHONE_VERIFICATION_REQUIRED"
  | "INVALID_PHONE_NUMBER"
  | "OTP_ALREADY_ACTIVE"
  | "TOO_MANY_REQUESTS"
  | "INVALID_OTP"
  | "OTP_EXPIRED"
  | "NO_PENDING_OTP"
  | "TOO_MANY_ATTEMPTS"
  | "PHONE_NUMBER_ALREADY_USED"
  | "PHONE_NUMBER_ALREADY_VALIDATED"
  | "SMS_UNAVAILABLE"
  | "DIRECTIONS_UNAVAILABLE"
  | "NO_ROUTE"
  | "SEARCH_UNAVAILABLE"
  | "INTERNAL";

export interface ApiError {
  error: ApiErrorCode;
  /** Localised text safe to display, when Pony provides one meant for the rider. */
  message?: string;
  retryAfter?: number;
}

export const VEHICLE_ID_RE = /^[A-Za-z0-9_-]{1,200}$/;
export const EMAIL_RE = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,}$/;

export type TripState = "IN_USE" | "PAUSED" | "FINISHED" | "CANCELLED";

export interface Position {
  lat: number;
  lng: number;
}

/** Live view of the rider's trip (screen 06). */
export interface TripView {
  id: string;
  vehicleId: string;
  state: TripState;
  startedAt: string;
  elapsedSec: number;
  holdCents: number;
  /** Plan frozen at the start of the trip, e.g. "1,00 € + 0,29 € / min". */
  priceLabel: string;
  estimatedPriceCents: number;
  /** From Pony's leg `asset.stateOfCharge` (see shared/range.ts). */
  rangeKm?: number;
  /** From GBFS, when Pony still lists the bike (it usually drops bikes in a trip). */
  vehicle?: { lat: number; lng: number; battery: number; rangeMeters: number };
}

/** A free 5-minute booking (TOMP one-stop): the countdown is server-computed to avoid clock skew. */
/** A route from Pony's direction API (OSRM), decoded server-side. */
export type DirectionsMode = "foot" | "bicycle";
export interface Directions {
  mode: DirectionsMode;
  distanceMeters: number;
  durationSec: number;
  path: Position[];
}

/** Address / place search (Mapbox Search Box, proxied). */
export interface PlaceSuggestion {
  id: string;
  name: string;
  /** Secondary line: street, city. */
  address?: string;
}
export interface PlaceView {
  name: string;
  address?: string;
  position: Position;
}

export interface BookingView {
  id: string;
  vehicleId: string;
  remainingSec: number;
  /** Full length of the booking, from Pony's Expires (for the countdown ring). */
  totalSec: number;
  /** The booked bike as a map pin: Pony drops it from the available bikes while it is booked. */
  vehicle?: Vehicle;
}

/** Result of START_FINISHING (D8). */
export interface EndCheck {
  allowed: boolean;
  verdict: string;
  warning?: string;
}

/** Ended trip (screen 08). */
export interface TripSummary {
  id: string;
  vehicleId: string;
  state: "FINISHED" | "CANCELLED";
  startedAt: string;
  endedAt: string;
  durationSec: number;
  distanceMeters?: number;
  priceCents: number;
  unlockCents: number;
  holdCents: number;
  /** Plan used for this trip and the minutes it billed. */
  priceLabel: string;
  billedMinutes: number;
  endedBy: "user" | "pony";
  parkingName?: string;
  /** Pony computes the distance minutes after the end: true until it shows up on the leg. */
  distancePending: boolean;
  /** START_FINISHING verdict (TAKE_PICTURE = in a parking zone; NOT_IN_PARKING_ZONE, …). */
  parking?: string;
}

export interface PaymentMethodView {
  brand: string;
  last4: string;
  wallet?: string;
}

export const HOLD_CENTS = 500;

/**
 * One finished ride of the trip history (Figma H2 row, H3 receipt). Pony's leg (TOMP
 * /bookings?state=FINISHED) is the source of truth; rides started from this app are enriched with
 * our own record (what we charged, the plan).
 */
export interface TripHistoryItem {
  /** Pony leg id (= our trip id for rides started here). */
  id: string;
  vehicleId: string;
  startedAt: string;
  endedAt: string;
  durationSec: number;
  billedMinutes: number;
  distanceMeters?: number;
  /** What we charged, else Pony's final fare; absent when neither is known. */
  priceCents?: number;
  /** Started from this app (paid with our payment method); false: made in the Pony app. */
  inApp: boolean;
  /** In-app rides only: unlock fee and plan label ("0,21 € / min, déverrouillage offert"). */
  unlockCents?: number;
  priceLabel?: string;
  /** Start and end positions from the leg (the receipt map and addresses). */
  from?: Position;
  to?: Position;
  /** Receipt only: the real track (Pony leg progressGeometry), thinned. */
  path?: Position[];
}

/** GET /api/trips?offset=&limit=: one page of history, newest first. */
export interface TripHistoryPage {
  items: TripHistoryItem[];
  /** Offset of the next page; absent on the last one. */
  nextOffset?: number;
}

/** GET /api/places/reverse?at=lng,lat: the address at a point (Mapbox reverse geocoding). */
export interface PlaceLabel {
  /** Short label: "12 Rue de la Loge", "Place de la Comédie". */
  name: string;
  /** Full address, when Mapbox gives one. */
  address?: string;
}
